import { Component, computed, inject, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ApiService } from '../../api.service';
import { InternalToolbar } from '../../components/internal-toolbar/internal-toolbar';
import { ReactiveIconTextButton } from '../../components/reactive-icon-text-button/reactive-icon-text-button';
import { TabsModule } from 'primeng/tabs';
import { TAGPresence } from '../../../models/tagpresence';
import { TAGUser } from '../../../models/taguser';
import { MatIcon } from '../../components/mat-icon/mat-icon';
import { Skeleton } from 'primeng/skeleton';
import { TAGPermission } from '../../../models/tagpermission';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from "@angular/forms";

@Component({
  selector: 'app-internal-presence',
  imports: [
    InternalToolbar,
    ReactiveIconTextButton,
    TranslatePipe,
    TabsModule,
    MatIcon,
    Skeleton,
    RouterLink,
    DatePipe,
    ToggleSwitchModule,
    FormsModule
],
  providers: [DialogService, TranslateService],
  templateUrl: './internal-presence.html',
  styleUrl: './internal-presence.scss',
})
export class InternalPresence {
  //private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  public api = inject(ApiService);

  showHiddenUsers = signal(false);

  presence = signal<TAGPresence[]>([]);
  loadingPresence = signal(true);

  users = signal<TAGUser[]>([]);
  loadingUsers = signal(true);

  schoolyears = computed(() => {
    var years: number[] = [];
    for (let p of this.presence()) {
      let y = p.getSchoolYear();
      if (!years.includes(y)) years.push(y);
    }
    return years.sort();
  });

  presencePerYear = computed(() => {
    let filteredPresence: { [year: string]: TAGPresence[] } = {};

    for (let year of this.schoolyears()) {
      filteredPresence[year + ''] = this.presence().filter((p) => {
        return p.getSchoolYear() == year;
      });
    }

    return filteredPresence;
  });

  usersPerYear = computed(() => {
    var users: { [year: string]: string[] } = {};
    let yearPresence = this.presencePerYear();

    for (let year of this.schoolyears()) {
      let pres = yearPresence[year];

      users[year + ''] = [];

      for (let p of pres) {
        for (let key of Object.keys(p.presenceInfo)) {
          if (
            !users[year + ''].includes(key) &&
            (this.showHiddenUsers() || !this.getUserHidden(key))
          )
            users[year + ''].push(key);
        }
      }
    }

    return users;
  });

  getPresenceColor(value: number) {
    return TAGPresence.getColor(value);
  }

  getPresenceIcon(value: number) {
    return TAGPresence.getIcon(value);
  }

  getUserName(username: string) {
    var usr = this.users().find((u) => u.username == username);
    if (usr != undefined) return usr.firstname + ' ' + usr.lastname;

    return username;
  }

  getUserHidden(user: string) {
    return this.users().find((u) => u.username == user)?.hidden ?? false;
  }

  getUserColor(presence: TAGPresence, user: string) {
    var alpha = this.canEdit(presence) ? '' : '99';
    return presence.getUserColor(user, alpha);
  }

  getUserIcon(presence: TAGPresence, user: string) {
    return presence.getUserIcon(user);
  }

  canEdit(presence: TAGPresence) {
    let locked = new Date().getTime() - presence.enddate.getTime() > 24 * 60 * 60 * 1000;
    if (locked) {
      if (!this.api.hasPermission(TAGPermission.edit_old_presence)) return false;
    }

    return this.api.hasPermission(TAGPermission.edit_presence);
  }

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.loadPresence();

      this.api
        .getUsers()
        .then((users: TAGUser[]) => {
          this.users.set(users);
          this.loadingUsers.set(false);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.translate('errors.couldNotLoadUsers')(),
            detail: reason.message,
          });
        });
    });
  }

  loadPresence() {
    this.loadingPresence.set(true);

    this.api
      .getPresences()
      .then((presences: TAGPresence[]) => {
        presences.sort((a: TAGPresence, b: TAGPresence) => a.startdate.getTime() - b.startdate.getTime());

        this.presence.set(presences);
        this.loadingPresence.set(false);
        console.log(presences, this.schoolyears(), this.presencePerYear());
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotLoadPresenceInformation')(),
          detail: reason.message,
        });
      });
  }

  newPresence() {
    this.router.navigate(["new"], {relativeTo: this.route});
  }

  getUserPercentage(user: string, year: number) {
    var presences = this.presencePerYear()[year];
    var total = presences.length;
    var present = 0;

    for (let p of presences) {
      if ([1, 2, 3, 4].includes(p.presenceInfo[user])) present++;
      if ([5].includes(p.presenceInfo[user])) present += 0.75;
    }

    return Math.round((present / total) * 100).toFixed(0);
  }

  extractSchoolyears(presence: TAGPresence[]) {
    var years: number[] = [];
    for (let p of presence) {
      let y = p.getSchoolYear();
      if (!years.includes(y)) years.push(y);
    }
    return years;
  }
}
